import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LoginIcon from '@mui/icons-material/Login';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import GavelIcon from '@mui/icons-material/Gavel';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DescriptionIcon from '@mui/icons-material/Description';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SyncIcon from '@mui/icons-material/Sync';
import { LogAuditoriaItem, TipoEventoAuditoria } from '../types/siniestros.types';

type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'purple';

export const TONE_STYLES: Record<Tone, { color: string; ring: string; bg: string; border: string }> = {
    neutral: { color: '#8390A2', ring: '#CDD3DA', bg: '#F8FAFC', border: '#E2E8F0' },
    info: { color: '#5182EF', ring: '#B9CDF9', bg: '#EFF6FF', border: '#BFDBFE' },
    success: { color: '#45B56E', ring: '#B5E1C5', bg: '#F0FDF4', border: '#BBF7D0' },
    warning: { color: '#E19238', ring: '#F3D3AF', bg: '#FFF7ED', border: '#FED7AA' },
    danger: { color: '#E35151', ring: '#F4B9B9', bg: '#FEF2F2', border: '#FECACA' },
    purple: { color: '#A95CEE', ring: '#DDBEF8', bg: '#FAF5FF', border: '#E9D5FF' }
};

export const TIPO_EVENTO_CONFIG: Record<TipoEventoAuditoria, { label: string; icon: React.ReactElement; tone: Tone }> = {
    INGRESO_SAS: { label: 'Ingreso SAS', icon: <LoginIcon />, tone: 'neutral' },
    CAMBIO_ESTADO: { label: 'Cambio de estado', icon: <SwapHorizIcon />, tone: 'info' },
    ASIGNACION_PREVENTOR: { label: 'Asignación preventor', icon: <PersonAddAltIcon />, tone: 'info' },
    REGISTRO_AUTORIZACION_UERT: { label: 'Autorización UERT', icon: <GavelIcon />, tone: 'purple' },
    INVESTIGACION_PRESENTADA: { label: 'Investigación presentada', icon: <AssignmentTurnedInIcon />, tone: 'success' },
    CARGA_DIGITAL_GUARDADA: { label: 'Carga digital', icon: <CloudUploadIcon />, tone: 'success' },
    MAIL_ENVIADO: { label: 'Mail enviado', icon: <MailOutlineIcon />, tone: 'neutral' },
    EXPORTACION_SRT: { label: 'Exportación SRT', icon: <UploadFileIcon />, tone: 'info' },
    CIERRE_ADMINISTRATIVO: { label: 'Cierre administrativo', icon: <TaskAltIcon />, tone: 'success' },
    PRAM_ACTIVADO: { label: 'PRAM activado', icon: <WarningAmberIcon />, tone: 'warning' },
    PRAM_DDJJ_CARGADA: { label: 'DDJJ PRAM', icon: <DescriptionIcon />, tone: 'warning' },
    INCUMPLIMIENTO_REGISTRADO: { label: 'Incumplimiento', icon: <ReportProblemIcon />, tone: 'danger' },
    SINCRONIZACION_VISITAS: { label: 'Sincronización visitas', icon: <SyncIcon />, tone: 'neutral' }
};

function formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    return date.toLocaleString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
    });
}

const StateBadge = ({ label, emphasis }: { label: string; emphasis?: boolean }) => (
    <Box
        component="span"
        sx={{
            px: 1,
            py: 0.25,
            borderRadius: 1,
            border: '1px solid',
            borderColor: emphasis ? '#CBD5E1' : '#E2E8F0',
            backgroundColor: '#FFFFFF',
            fontSize: 11,
            fontWeight: emphasis ? 700 : 500,
            color: emphasis ? '#334155' : '#64748B',
            whiteSpace: 'nowrap'
        }}
    >
        {label}
    </Box>
);

interface Props {
    entrada: LogAuditoriaItem;
    expanded: boolean;
    onToggle: () => void;
}

export function LogAuditoriaEntry({ entrada, expanded, onToggle }: Props) {
    const config = TIPO_EVENTO_CONFIG[entrada.tipoEvento];
    const tipoLabel = config?.label ?? entrada.tipoEvento;
    const tone = TONE_STYLES[config?.tone ?? 'neutral'];
    const hasDetail = Boolean(entrada.estadoAnterior || entrada.estadoNuevo || entrada.notasAdicionales);

    return (
        <Accordion
            expanded={expanded}
            onChange={onToggle}
            disableGutters
            elevation={0}
            sx={{
                backgroundColor: 'transparent',
                '&:before': { display: 'none' }
            }}
        >
            <AccordionSummary
                expandIcon={hasDetail ? <ExpandMoreIcon sx={{ color: '#94A3B8' }} /> : null}
                sx={{
                    px: 0,
                    py: 0.5,
                    minHeight: 'auto',
                    backgroundColor: 'transparent',
                    '& .MuiAccordionSummary-content': { my: 0 }
                }}
            >
                <Box sx={{ width: '100%' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1E293B' }}>
                        {tipoLabel}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#94A3B8', mt: 0.25 }}>
                        {formatTimestamp(entrada.timestamp)} ·{' '}
                        <Box component="span" sx={{ fontWeight: 500, color: '#64748B' }}>
                            {entrada.actor}
                        </Box>
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: '#475467', mt: 0.5 }}>
                        {entrada.resumen}
                    </Typography>
                </Box>
            </AccordionSummary>
            {hasDetail && (
                <AccordionDetails sx={{ px: 0, pt: 1, pb: 0 }}>
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 1.75,
                            border: '1px solid',
                            borderColor: tone.border,
                            backgroundColor: tone.bg,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1
                        }}
                    >
                        {(entrada.estadoAnterior || entrada.estadoNuevo) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {entrada.estadoAnterior && <StateBadge label={entrada.estadoAnterior} />}
                                {entrada.estadoAnterior && entrada.estadoNuevo && (
                                    <ArrowForwardIcon sx={{ fontSize: 14, color: '#94A3B8' }} />
                                )}
                                {entrada.estadoNuevo && <StateBadge label={entrada.estadoNuevo} emphasis />}
                            </Box>
                        )}
                        {entrada.notasAdicionales && (
                            <Typography sx={{ fontSize: 13, color: '#475467' }}>
                                {entrada.notasAdicionales}
                            </Typography>
                        )}
                        <Typography sx={{ fontSize: 10, color: '#94A3B8', fontFamily: 'monospace' }}>
                            ID: {entrada.id}
                        </Typography>
                    </Box>
                </AccordionDetails>
            )}
        </Accordion>
    );
}
